import { useCallback, useEffect, useState } from 'react';
import type { RolAviso, TipoAviso } from '../../api/ops/entregas';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Deshacer } from '../../componentes/Deshacer';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Ventana } from '../../componentes/Ventana';
import { conY, rolesDe } from '../../logica/acceso';
import { useEnvioRetrasado } from '../../logica/envioRetrasado';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import { Menu } from '../../componentes/Menu';
import {
    PERMISOS_AVISOS,
    bloqueadoPara,
    cuantosRecibe,
    porModulo,
    rolesDeAvisos,
    rolesTrasCambio,
    tiposCopiados,
    tiposDeActivarTodos,
    versionesBase,
    type RolDeAvisos,
} from './reglas';
import { AYUDA_QUIEN_RECIBE, COLOR_PUNTO } from './textos';

/**
 * «Quién recibe avisos» (5.2, G294): superficie partida con los roles (el propietario, los predefinidos y los propios,
 * G37) y «Recibe N de M»; a la derecha, los tipos del rol elegido agrupados por módulo (G299) con su RV Interruptor; los
 * críticos del propietario, «Siempre» con candado. Cada cambio sale a los 5 s con «Deshacer» (G159, es un ajuste: no
 * avisa a nadie) con `updateNoticeTypeRecipients` e `If-Match` de su versión (`0` sin cambiar). «Activar todos» y
 * «Copiar de otro rol» mandan la lista completa del rol con las versiones leídas (`updateRoleNoticeTypes`, G300). Nunca
 * deja un tipo sin nadie. «Ver por aviso», la lista al revés. Solo lectura: interruptores desactivados.
 */
export function QuienRecibe(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, membresia, nombreNegocio, navegar } = props;
    const permisos = usePermisos();
    const puede = permisos.puede(PERMISOS_AVISOS.configurar);
    const avisar = useAvisoBreve();
    const envio = useEnvioRetrasado();
    const [tipos, setTipos] = useState<TipoAviso[] | null>(null);
    const [roles, setRoles] = useState<RolDeAvisos[] | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [elegido, setElegido] = useState<RolAviso | null>(null);
    const [porAviso, setPorAviso] = useState(false);
    const [avisoAbierto, setAvisoAbierto] = useState<string | null>(null);
    const [desplegado, setDesplegado] = useState<string | null>(null);
    const [copiar, setCopiar] = useState(false);

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        if (!puede) {
            return;
        }
        let vivo = true;
        Promise.all([ops.listNoticeTypes(negocio), ops.listRoles(negocio, { limit: 100 })])
            .then(([t, r]) => {
                if (vivo) {
                    setTipos(t.data);
                    setRoles(rolesDeAvisos(r.data));
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, puede, intento]);

    useCabecera({
        ayuda: puede ? AYUDA_QUIEN_RECIBE : [],
        acciones: puede ? [{ clave: 'vista', texto: porAviso ? 'Ver por rol' : 'Ver por aviso', icono: 'list', secundaria: true, onClick: () => { setPorAviso((v) => !v); } }] : [],
    });

    if (!puede) {
        return <EstadoSinPermiso seccion="Quién recibe avisos" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    if (error !== null && tipos === null) {
        return <EstadoError seccion="quién recibe avisos" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (tipos === null || roles === null) {
        return <EstadoCargando seccion="quién recibe avisos" />;
    }

    const fallo = (e: unknown, texto: string) => {
        const breve = avisoBreveDeError(e);
        avisar({ tipo: 'error', texto: breve ?? (e instanceof ErrorApi && (e.estado === 412 || e.estado === 409) ? 'Otra persona lo ha cambiado. Te enseñamos lo último.' : e instanceof ErrorApi && e.estado === 422 ? (e.problema.detail ?? texto) : texto) });
        recargar();
    };

    /** Enciende o apaga un tipo para un rol: se ve al momento y sale a los 5 s salvo «Deshacer». */
    const cambiar = (tipo: TipoAviso, rol: RolAviso, encender: boolean) => {
        const cambio = rolesTrasCambio(tipo, rol, encender);
        if ('error' in cambio) {
            avisar({ tipo: 'error', texto: cambio.error });
            return;
        }
        const antes = tipos;
        setTipos(tipos.map((t) => (t.notice_type === tipo.notice_type ? { ...t, roles: cambio.roles } : t)));
        envio.programar({
            texto: encender ? `«${tipo.title ?? tipo.notice_type}» llegará también a este rol.` : `«${tipo.title ?? tipo.notice_type}» ya no llegará a este rol.`,
            donde: 'ajuste',
            enviar: () => {
                ops.updateNoticeTypeRecipients(negocio, tipo.notice_type, cambio.roles, tipo.version)
                    .then((nuevo) => {
                        setTipos((ts) => (ts ?? []).map((t) => (t.notice_type === nuevo.notice_type ? nuevo : t)));
                    })
                    .catch((e: unknown) => {
                        fallo(e, 'No he podido guardar el cambio. Prueba de nuevo.');
                    });
            },
            alDeshacer: () => {
                setTipos(antes);
            },
        });
    };

    /** «Activar todos» o «Copiar de otro rol»: la lista completa del rol, todo o nada. */
    const guardarRol = (rol: RolAviso, codigos: string[], texto: string) => {
        const antes = tipos;
        const versiones = versionesBase(tipos);
        setTipos(tipos.map((t) => ({ ...t, roles: codigos.includes(t.notice_type) ? [...new Set([...t.roles, rol])] : t.roles.filter((r) => r !== rol) })));
        envio.programar({
            texto,
            donde: 'ajuste',
            enviar: () => {
                ops.updateRoleNoticeTypes(negocio, rol, codigos, versiones)
                    .then((l) => {
                        setTipos((ts) => (ts ?? []).map((t) => l.data.find((x) => x.notice_type === t.notice_type) ?? t));
                    })
                    .catch((e: unknown) => {
                        fallo(e, 'No he podido guardar los avisos de este rol. Prueba de nuevo.');
                    });
            },
            alDeshacer: () => {
                setTipos(antes);
            },
        });
    };

    const rol = roles.find((r) => r.ref === elegido) ?? (movil ? null : (roles[0] ?? null));
    const total = tipos.length;
    const nombreDe = (ref: RolAviso) => roles.find((r) => r.ref === ref)?.nombre ?? ref;
    const deshacer = envio.pendiente !== null && (
        <div className="rv-ca-hecho" role="status">
            <span className="rv-ca-hecho__baldosa"><Icono nombre="check" /></span>
            <span style={{ flex: 1 }}>{envio.pendiente.texto}</span>
            <Deshacer onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} movil={movil} />
        </div>
    );

    if (porAviso) {
        const tipo = tipos.find((t) => t.notice_type === avisoAbierto) ?? null;
        return (
            <div className="rv-ca-apartado">
                {deshacer}
                {porModulo(tipos).map((g) => (
                    <section key={g.modulo} className="rv-ca-bloque" aria-label={g.nombre}>
                        <div className="rv-ca-bloque__cab"><strong>{g.nombre}</strong></div>
                        <div className="rv-ca-lista">
                            {g.tipos.map((t) => (
                                <div key={t.notice_type} className="rv-ca-fila-caja">
                                    <button type="button" className="rv-ca-fila rv-ca-fila--abre rv-en-fila-b" onClick={() => { setAvisoAbierto(t.notice_type); }}>
                                        <span className="rv-en-punto" style={{ background: COLOR_PUNTO[t.severity] }} aria-hidden="true" />
                                        <span className="rv-ca-fila__texto">
                                            <strong>{t.title ?? t.notice_type}</strong>
                                            <span>{conY(t.roles.map(nombreDe))}</span>
                                        </span>
                                        <Icono nombre="chevron-right" className="rv-ca-chevron" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </section>
                ))}
                <PanelLateral abierto={tipo !== null} onCerrar={() => { setAvisoAbierto(null); }} movil={movil} titulo={tipo?.title ?? ''} eyebrow="Aviso" icono="bell" subtitulo={tipo?.description ?? undefined}>
                    {tipo !== null && (
                        <div className="rv-ca-bloque">
                            {roles.map((r) => (
                                <FilaInterruptor key={r.ref} titulo={r.nombre} tipo={tipo} rol={r.ref} soloLectura={soloLectura} onCambio={(v) => { cambiar(tipo, r.ref, v); }} />
                            ))}
                        </div>
                    )}
                </PanelLateral>
            </div>
        );
    }

    const listaRoles = (
        <div className="rv-en-roles" role="list" aria-label="Roles">
            {roles.map((r) => (
                <button key={r.ref} type="button" role="listitem" aria-current={rol?.ref === r.ref} className={`rv-en-rol${rol?.ref === r.ref ? ' rv-en-rol--on' : ''}`} onClick={() => { setElegido(r.ref); setDesplegado(null); }}>
                    <span className="rv-ca-baldosa"><Icono nombre={r.ref === 'owner' ? 'crown' : r.propio ? 'user-cog' : 'user'} /></span>
                    <span className="rv-ca-fila__texto">
                        <strong>{r.nombre}</strong>
                        <span>{`${r.propio ? 'Rol propio · ' : ''}Recibe ${String(cuantosRecibe(tipos, r.ref))} de ${String(total)}`}</span>
                    </span>
                </button>
            ))}
        </div>
    );

    const detalle = rol === null ? null : (
        <div className="rv-en-detalle">
            <div className="rv-en-detalle__cab">
                <span className="rv-ca-baldosa"><Icono nombre={rol.ref === 'owner' ? 'crown' : 'user'} /></span>
                <span className="rv-ca-fila__texto">
                    <strong>{rol.nombre}</strong>
                    <span>{`Recibe ${String(cuantosRecibe(tipos, rol.ref))} de ${String(total)} avisos`}</span>
                </span>
                {!soloLectura && (
                    <>
                        <Boton tipo="secundario" icono="bell-ring" movil={movil} onClick={() => { guardarRol(rol.ref, tiposDeActivarTodos(tipos), `${rol.nombre} recibirá todos los avisos.`); }}>
                            Activar todos
                        </Boton>
                        <Menu etiqueta={`Más acciones de ${rol.nombre}`} movil={movil} opciones={[{ clave: 'copiar', texto: 'Copiar de otro rol', icono: 'copy', onClick: () => { setCopiar(true); } }]} />
                    </>
                )}
            </div>
            {porModulo(tipos).map((g) => (
                <section key={g.modulo} className="rv-ca-bloque rv-en-modulo" aria-label={g.nombre}>
                    <div className="rv-ca-bloque__cab"><strong>{g.nombre}</strong></div>
                    {g.tipos.map((t) => (
                        <div key={t.notice_type}>
                            <FilaInterruptor
                                titulo={t.title ?? t.notice_type}
                                punto={COLOR_PUNTO[t.severity]}
                                tipo={t}
                                rol={rol.ref}
                                soloLectura={soloLectura}
                                desplegado={desplegado === t.notice_type}
                                onDesplegar={() => { setDesplegado((d) => (d === t.notice_type ? null : t.notice_type)); }}
                                onCambio={(v) => { cambiar(t, rol.ref, v); }}
                            />
                            {desplegado === t.notice_type && (
                                <div className="rv-ca-caja rv-en-explica">
                                    {t.description != null && <span>{t.description}</span>}
                                    <span>{`Lo reciben: ${conY(t.roles.map(nombreDe)) || 'nadie'}.`}</span>
                                    {t.email_owner && <span>Además, llega por email al propietario.</span>}
                                    {t.scoped_to_related_member && <span>El profesional y el RRPP solo reciben los suyos.</span>}
                                </div>
                            )}
                        </div>
                    ))}
                </section>
            ))}
        </div>
    );

    return (
        <div className="rv-ca-apartado">
            {deshacer}
            {movil ? (
                <>
                    {listaRoles}
                    <PanelLateral abierto={elegido !== null} onCerrar={() => { setElegido(null); }} movil tamano="ficha" titulo={rol?.nombre ?? ''} eyebrow="Quién recibe avisos" icono="bell-ring">
                        {detalle}
                    </PanelLateral>
                </>
            ) : (
                <div className="rv-en-partida">
                    {listaRoles}
                    {detalle}
                </div>
            )}
            {copiar && rol !== null && <VentanaCopiar destino={rol} roles={roles} movil={movil} onCerrar={() => { setCopiar(false); }} onCopiar={(origen) => { setCopiar(false); guardarRol(rol.ref, tiposCopiados(tipos, origen.ref, rol.ref), `${rol.nombre} recibirá los mismos avisos que ${origen.nombre}.`); }} />}
        </div>
    );
}

function FilaInterruptor({ titulo, punto, tipo, rol, soloLectura, desplegado, onDesplegar, onCambio }: { titulo: string; punto?: string; tipo: TipoAviso; rol: RolAviso; soloLectura: boolean; desplegado?: boolean; onDesplegar?: () => void; onCambio: (v: boolean) => void }) {
    const siempre = bloqueadoPara(tipo, rol);
    return (
        <div className="rv-en-tipo">
            {punto !== undefined && <span className="rv-en-punto" style={{ background: punto }} aria-hidden="true" />}
            <strong className="rv-en-tipo__t">{titulo}</strong>
            {onDesplegar !== undefined && (
                <button type="button" className="rv-bi rv-en-tipo__mas" aria-label={`Qué es «${titulo}»`} aria-expanded={desplegado} onClick={onDesplegar}>
                    <Icono nombre={desplegado === true ? 'chevron-up' : 'chevron-down'} />
                </button>
            )}
            {siempre ? (
                <span className="rv-en-siempre"><Icono nombre="lock" />Siempre</span>
            ) : (
                <Interruptor ariaLabel={`${titulo}: ${rol}`} valor={tipo.roles.includes(rol)} desactivado={soloLectura} compacto linea="ninguna" onChange={onCambio} />
            )}
        </div>
    );
}

function VentanaCopiar({ destino, roles, movil, onCerrar, onCopiar }: { destino: RolDeAvisos; roles: RolDeAvisos[]; movil: boolean; onCerrar: () => void; onCopiar: (origen: RolDeAvisos) => void }) {
    const otros = roles.filter((r) => r.ref !== destino.ref);
    const [origen, setOrigen] = useState(otros[0]?.ref ?? '');
    const elegido = otros.find((r) => r.ref === origen);
    return (
        <Ventana
            abierta
            titulo="Copiar de otro rol"
            texto={`${destino.nombre} recibirá los mismos avisos que el rol que elijas. Los críticos siguen llegando al propietario.`}
            accion="Copiar"
            accionIcono="copy"
            inactiva={elegido === undefined}
            movil={movil}
            onAccion={() => {
                if (elegido !== undefined) {
                    onCopiar(elegido);
                }
            }}
            onCerrar={onCerrar}
        >
            <Desplegable etiqueta="Copiar de" valor={origen} opciones={otros.map((r) => ({ valor: r.ref, texto: r.nombre }))} onChange={setOrigen} />
        </Ventana>
    );
}
